import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  Button,
} from "@mui/material";
import { useLocation, Link } from "react-router-dom";

const ReconciliationDetails = () => {
  const location = useLocation();
  const { row } = location.state || {};

  if (!row) {
    return <Typography>No data provided.</Typography>;
  }

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleString();
    } catch {
      return "N/A";
    }
  };

  return (
    <Card sx={{ maxWidth: "95%", margin: "auto", mt: 4, p: 2 }}>
      <CardContent>
        <Typography variant="h4" gutterBottom>
          Reconciliation Details
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* General Info */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography><strong>Reconciliation ID:</strong> {row?.reconciliationId || "N/A"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography><strong>Status:</strong> {row?.reconciliationStatus || "N/A"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography><strong>Collector App ID:</strong> {row?.collectorAppId || "N/A"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography><strong>Receiver App ID:</strong> {row?.receiverAppId || "N/A"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography><strong>Created At:</strong> {formatDate(row?.createdAt)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography><strong>Updated At:</strong> {formatDate(row?.updatedAt)}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Orders */}
        <Typography variant="h6">Orders</Typography>
        {row?.orders?.length > 0 ? (
          row.orders.map((order, idx) => (
            <Box key={idx} sx={{ my: 2, pl: 2, borderLeft: "3px solid #2196f3" }}>
              <Typography><strong>Order ID:</strong> {order?.id || "N/A"}</Typography>
              <Typography>
                <strong>Amount:</strong> ₹{order?.amount?.value || "0"} {order?.amount?.currency || ""}
              </Typography>
              <Typography><strong>Recon Accord:</strong> {order?.recon_accord ? "Yes" : "No"}</Typography>

              {/* Settlements */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle1">Settlements:</Typography>
                {order?.settlements?.length > 0 ? (
                  order.settlements.map((s, sIdx) => (
                    <Box key={sIdx} sx={{ my: 1, pl: 2, borderLeft: "2px dashed #999" }}>
                      <Typography><strong>Settlement ID:</strong> {s?.id || "N/A"}</Typography>
                      <Typography><strong>Payment ID:</strong> {s?.payment_id || "N/A"}</Typography>
                      <Typography><strong>Status:</strong> {s?.status || "N/A"}</Typography>
                      <Typography>
                        <strong>Amount:</strong> ₹{s?.amount?.value || "0"} {s?.amount?.currency || ""}
                        {s?.amount?.diff_value && ` (Diff: ₹${s.amount.diff_value})`}
                      </Typography>
                      <Typography>
                        <strong>Commission:</strong> ₹{s?.commission?.value || "0"} {s?.commission?.currency || ""}
                        {s?.commission?.diff_value && ` (Diff: ₹${s.commission.diff_value})`}
                      </Typography>
                      <Typography>
                        <strong>Withholding:</strong> ₹{s?.withholding_amount?.value || "0"} {s?.withholding_amount?.currency || ""}
                        {s?.withholding_amount?.diff_value && ` (Diff: ₹${s.withholding_amount.diff_value})`}
                      </Typography>
                      <Typography>
                        <strong>TCS:</strong> ₹{s?.tcs?.value || "0"} {s?.tcs?.currency || ""}
                        {s?.tcs?.diff_value && ` (Diff: ₹${s.tcs.diff_value})`}
                      </Typography>
                      <Typography>
                        <strong>TDS:</strong> ₹{s?.tds?.value || "0"} {s?.tds?.currency || ""}
                        {s?.tds?.diff_value && ` (Diff: ₹${s.tds.diff_value})`}
                      </Typography>
                      <Typography><strong>Due Date:</strong> {s?.due_date || "N/A"}</Typography>
                      <Typography><strong>Updated At:</strong> {formatDate(s?.updated_at)}</Typography>
                      <Typography><strong>Collector App ID:</strong> {s?.collectorAppId || "N/A"}</Typography>
                      <Typography><strong>Receiver App ID:</strong> {s?.receiverAppId || "N/A"}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography>No settlements found.</Typography>
                )}
              </Box>
            </Box>
          ))
        ) : (
          <Typography>No orders found.</Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Context */}
        {row?.context && (
          <>
            <Typography variant="h6">Context</Typography>
            <Box sx={{ my: 1, pl: 2, borderLeft: "2px solid #9c27b0" }}>
              <Typography><strong>Domain:</strong> {row?.context?.domain || "N/A"}</Typography>
              <Typography><strong>Version:</strong> {row?.context?.version || "N/A"}</Typography>
              <Typography><strong>Action:</strong> {row?.context?.action || "N/A"}</Typography>
              <Typography><strong>BAP ID:</strong> {row?.context?.bap_id || "N/A"}</Typography>
              <Typography><strong>BPP ID:</strong> {row?.context?.bpp_id || "N/A"}</Typography>
              <Typography>
                <strong>Location:</strong> 
                {row?.context?.location?.country?.code || "N/A"},{" "}
                {row?.context?.location?.city?.code || "N/A"}
              </Typography>
              <Typography><strong>Message ID:</strong> {row?.context?.message_id || "N/A"}</Typography>
              <Typography><strong>Transaction ID:</strong> {row?.context?.transaction_id || "N/A"}</Typography>
              <Typography><strong>TTL:</strong> {row?.context?.ttl || "N/A"}</Typography>
            </Box>
          </>
        )}

        {/* Back button */}
        <Box sx={{ mb: 2, mt: 4 }}>
          <Button
            component={Link}
            to="/app/management/discrepancies-list"
            variant="outlined"
            color="primary"
          >
            Back
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReconciliationDetails;
